import { Suspense, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { HardHat, Bell, Menu, LogOut, ChevronsLeft, ChevronsRight } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Loading } from "./common";
import { useAuth } from "../lib/auth";
import { cn } from "../lib/utils";
import { menuFor, BOTTOM, slug } from "../lib/navigation";
import { HideAmountsButton, MoneySwitches, UserMenu } from "./layout/UserControls";
import FallbackDbBanner from "./layout/FallbackDbBanner";

const Brand = ({ compact }) => (
  <Link to="/" className="flex items-center gap-2.5 px-1" data-testid="brand-link">
    <div className="h-9 w-9 shrink-0 rounded-lg bg-amber-500 flex items-center justify-center text-slate-950">
      <HardHat className="h-5 w-5" strokeWidth={2.4} />
    </div>
    {!compact && (
      <div className="leading-tight">
        <div className="font-heading font-extrabold tracking-tight text-[15px]">CANTIERE <span className="text-amber-400">MANAGER</span> PRO</div>
        <div className="text-[10px] text-muted-foreground">Gestione operai, mezzi e cantieri</div>
      </div>
    )}
  </Link>
);

function NavItems({ items, compact, onNavigate, unread }) {
  return (
    <nav className="flex flex-col gap-1">
      {items.map(([to, label, Icon]) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/"}
          onClick={onNavigate}
          title={label}
          data-testid={`nav-${slug(to)}`}
          className={({ isActive }) =>
            cn(
              "group relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
              compact && "justify-center px-0",
              isActive ? "bg-amber-500/12 text-amber-300 ring-1 ring-amber-500/30" : "text-slate-300 hover:bg-accent hover:text-white"
            )
          }
        >
          <Icon className="h-[18px] w-[18px] shrink-0" />
          {!compact && <span className="truncate">{label}</span>}
          {to === "/notifiche" && unread > 0 && (
            <span className={cn("ml-auto rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white num", compact && "absolute right-1 top-1")}>{unread}</span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function Sidebar({ items, unread, collapsed, onToggle }) {
  return (
    <aside
      className={cn(
        "no-print hidden md:flex fixed inset-y-0 left-0 z-30 flex-col border-r border-border bg-[#0d121c]/95 backdrop-blur-md transition-[width] duration-200",
        collapsed ? "w-[72px]" : "w-[72px] lg:w-64"
      )}
      data-testid="sidebar"
    >
      <div className="h-16 flex items-center px-4 border-b border-border">
        <div className={cn(collapsed ? "" : "lg:hidden")}><Brand compact /></div>
        {!collapsed && <div className="hidden lg:block"><Brand /></div>}
      </div>
      <div className="flex-1 overflow-y-auto scrollbar-thin p-3">
        <div className={cn(collapsed ? "" : "lg:hidden")}><NavItems items={items} compact unread={unread} /></div>
        {!collapsed && <div className="hidden lg:block"><NavItems items={items} unread={unread} /></div>}
      </div>
      <button onClick={onToggle} className="hidden lg:flex items-center justify-center gap-2 border-t border-border py-3 text-xs text-muted-foreground hover:text-white" data-testid="sidebar-collapse-btn">
        {collapsed ? <ChevronsRight className="h-4 w-4" /> : <><ChevronsLeft className="h-4 w-4" /> Riduci menu</>}
      </button>
    </aside>
  );
}

const TODAY_LABEL = new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

function TopBar({ user, unread, onMenu, onLogout }) {
  const nav = useNavigate();
  return (
    <header className="no-print sticky top-0 z-20 h-16 flex items-center gap-3 border-b border-border bg-background/90 backdrop-blur-md px-4 md:px-6">
      <button className="md:hidden p-2 -ml-2 rounded-lg hover:bg-accent" onClick={onMenu} data-testid="mobile-menu-btn" aria-label="Menu"><Menu className="h-6 w-6" /></button>
      <div className="md:hidden"><Brand compact /></div>
      <div className="hidden md:block text-sm text-muted-foreground">{TODAY_LABEL}</div>
      <div className="ml-auto flex items-center gap-2">
        <HideAmountsButton />
        <button onClick={() => nav("/notifiche")} className="relative p-2.5 rounded-lg hover:bg-accent" data-testid="topbar-notifications-btn" aria-label="Notifiche">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 rounded-full bg-red-500 text-[11px] font-bold text-white flex items-center justify-center px-1 num" data-testid="notification-count">{unread}</span>
          )}
        </button>
        <UserMenu user={user} onLogout={onLogout} />
      </div>
    </header>
  );
}

function BottomNav({ items, onMore }) {
  return (
    <nav className="no-print md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-border bg-[#0d121c]/95 backdrop-blur-md grid grid-cols-5 pb-[env(safe-area-inset-bottom)]" data-testid="bottom-nav">
      {items.map(([to, label, Icon]) => (
        <NavLink key={to} to={to} end={to === "/"} data-testid={`bottom-nav-${slug(to)}`}
          className={({ isActive }) => cn("flex flex-col items-center justify-center gap-1 py-2.5 min-h-[60px] text-[10px] font-medium", isActive ? "text-amber-400" : "text-slate-400")}>
          <Icon className="h-6 w-6" />
          <span className="truncate max-w-[72px]">{label.replace("Dashboard Operativa", "Operativa")}</span>
        </NavLink>
      ))}
      <button onClick={onMore} className="flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-medium text-slate-400" data-testid="bottom-nav-more">
        <Menu className="h-6 w-6" /> Altro
      </button>
    </nav>
  );
}

function MobileMenu({ open, onOpenChange, items, unread, onLogout }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-[290px] bg-[#0d121c] p-4">
        <SheetHeader className="mb-4"><SheetTitle className="text-left"><Brand /></SheetTitle></SheetHeader>
        <NavItems items={items} unread={unread} onNavigate={() => onOpenChange(false)} />
        <MoneySwitches />
        <button onClick={onLogout} className="mt-6 w-full flex items-center gap-3 rounded-lg px-3 py-3 text-sm text-red-400 hover:bg-accent" data-testid="mobile-logout-btn">
          <LogOut className="h-5 w-5" /> Esci
        </button>
      </SheetContent>
    </Sheet>
  );
}

export default function Layout() {
  const { user, logout, unread } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const nav = useNavigate();
  const items = menuFor(user);
  const bottom = items.filter(([to]) => BOTTOM[user.role].includes(to));

  const doLogout = async () => {
    await logout();
    toast.success("Disconnessione effettuata");
    nav("/login");
  };

  return (
    <div className="min-h-screen flex">
      <Sidebar items={items} unread={unread} collapsed={collapsed} onToggle={() => setCollapsed((c) => !c)} />

      <div className={cn("flex-1 min-w-0 flex flex-col", collapsed ? "md:pl-[72px]" : "md:pl-[72px] lg:pl-64")}>
        <TopBar user={user} unread={unread} onMenu={() => setMobileOpen(true)} onLogout={doLogout} />
        <FallbackDbBanner isAdmin={user.role === "admin"} />

        <main className="flex-1 p-4 md:p-6 lg:p-8 pb-28 md:pb-10 max-w-[1600px] w-full mx-auto">
          <Suspense fallback={<Loading />}><Outlet /></Suspense>
        </main>
      </div>

      <BottomNav items={bottom} onMore={() => setMobileOpen(true)} />
      <MobileMenu open={mobileOpen} onOpenChange={setMobileOpen} items={items} unread={unread} onLogout={doLogout} />
    </div>
  );
}
